// components/Gallery.tsx
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import uploadIcon from '../assets/images/upload.svg';
import downloadIcon from '../assets/images/download.svg';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { AnimatePresence, motion } from 'framer-motion';

const TOAST_MS = 5000;
// Wylicza URL miniatury .jpg obok wideo
const posterUrlFromVideo = (videoUrl: string) => {
  const fileName = videoUrl.split('/').pop()?.replace(/\.(mp4|mov|m4v|webm)(\?.*)?$/i, '.jpg');
  return `https://nataliadominik.pl/backend/uploads/posters/${fileName}`;
};
// Generuje miniaturę z 1. sekundy filmu (ok. 320px szerokości)
async function captureVideoPoster(file: File, timeSec = 1): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.src = url;
    video.playsInline = true;

    const cleanup = () => URL.revokeObjectURL(url);

    video.addEventListener('loadedmetadata', () => {
      const t = Math.min(timeSec, video.duration || timeSec);
      video.currentTime = isFinite(t) ? t : 0.5;
    });

    video.addEventListener('seeked', () => {
      try {
        const targetW = 320;
        const ratio = (video.videoWidth || 16) / (video.videoHeight || 9);
        const w = targetW;
        const h = Math.round(targetW / ratio);

        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D not available');
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob((blob) => {
          cleanup();
          blob ? resolve(blob) : reject(new Error('Nie udało się utworzyć miniatury'));
        }, 'image/jpeg', 0.82);
      } catch (e) {
        cleanup();
        reject(e);
      }
    });

    video.addEventListener('error', () => {
      cleanup();
      reject(new Error('Błąd odczytu wideo do miniatury'));
    });
  });
}

const Gallery: React.FC<{ setFullscreenGallery?: (v: boolean) => void }> = ({ setFullscreenGallery }) => {
  const [media, setMedia] = useState<Array<{ url: string; timestamp: string; type: 'image' | 'video' }>>([]);
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0–100 (łączny postęp)
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [errorBarVisible, setErrorBarVisible] = useState(false);
  const totalBytesRef = useRef<number>(0);
  const loadedBytesRef = useRef<number>(0);
  const touchStartX = useRef<number | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const thumbnailsBarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!uploadError) return;
    const t = window.setTimeout(() => setUploadError(null), TOAST_MS);
    return () => window.clearTimeout(t);
  }, [uploadError]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadError(null);
    setIsUploading(true);
    setUploadProgress(0);
    totalBytesRef.current = Array.from(files).reduce((acc, f) => acc + f.size, 0);
    loadedBytesRef.current = 0;

    const timestamp = new Date().toISOString();

    // Helper: upload jednego pliku z progressem
    const uploadSingle = async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('timestamp', timestamp);

      // jeśli to wideo — najpierw wygeneruj poster (await poza new Promise)
      if (file.type.startsWith('video/')) {
        try {
          const posterBlob = await captureVideoPoster(file, 1);
          const base = (file.name || 'video').replace(/\.[^/.]+$/, '');
          formData.append('poster', posterBlob, `${base}.jpg`);
        } catch (e) {
          console.warn('Poster gen failed:', e);
        }
      }

      // teraz wykonaj upload — tu już NIE używamy await w executorze Promise
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', 'https://nataliadominik.pl/backend/upload.php');

        // 10 minut
        xhr.timeout = 10 * 60 * 1000;

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const delta = event.loaded - (xhr as any).__lastLoaded || 0;
            (xhr as any).__lastLoaded = event.loaded;

            loadedBytesRef.current = Math.min(
              totalBytesRef.current,
              loadedBytesRef.current + Math.max(delta, 0)
            );
            const pct = Math.round((loadedBytesRef.current / totalBytesRef.current) * 100);
            setUploadProgress(isNaN(pct) ? 0 : pct);
          }
        };

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(xhr.responseText || 'Błąd przesyłania.'));
          }
        };

        xhr.ontimeout = () => reject(new Error('Przekroczono czas połączenia.'));
        xhr.onerror = () => reject(new Error('Błąd sieci podczas wysyłania.'));

        xhr.send(formData);
      });
    };;

    try {
      for (const file of Array.from(files)) {
        await uploadSingle(file);
      }

      // krótka pauza po zakończeniu transferu – backend/CDN często potrzebuje chwili
      await new Promise(r => setTimeout(r, 400));

      await fetchMedia(); // odśwież listę

      // Dodatkowo przeleć po mediach typu video i spróbuj je "obudzić"
      const videos = (Array.isArray(media) ? media : []).filter(m => m.type === 'video');
      for (const v of videos.slice(-Math.min(videos.length, 5))) {
        await waitForAvailability(v.url);
      }

      setUploadProgress(100);
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        (e.target as HTMLInputElement).value = '';
      }, 600);
    } catch (err: any) {
        const msg = err?.message ?? 'Błąd przesyłania plików.';
        setIsUploading(false);
        setUploadProgress(0);

        // pokaż treść w toascie / bannerze:
        setUploadError(msg);            // <-- ZOSTAWIAMY, nie czyścimy tutaj

        // odpal czerwony pasek na chwilę:
        setErrorBarVisible(true);
        setUploadProgress(0);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setUploadProgress(100));
        });

        try { (navigator as any)?.vibrate?.(30); } catch {}

        const ERROR_SWEEP_MS = 550;
        const EXTRA_HANG_MS  = 300;
        window.setTimeout(() => {
          setErrorBarVisible(false);    // pasek znika
          setUploadProgress(0);
          loadedBytesRef.current = 0;
          totalBytesRef.current  = 0;
          (e.target as HTMLInputElement).value = '';
        }, ERROR_SWEEP_MS + EXTRA_HANG_MS);
    }
  };

  async function waitForAvailability(url: string, tries = 8, delayMs = 800): Promise<boolean> {
    for (let i = 0; i < tries; i++) {
      try {
        // Najpierw HEAD
        const head = await fetch(url, { method: 'HEAD', cache: 'no-store' });
        if (head.ok || head.status === 206) return true;

        // fallback: mały range GET (niektóre serwery lepiej to obsługują)
        const rng = await fetch(url, {
          method: 'GET',
          headers: { Range: 'bytes=0-1' },
          cache: 'no-store',
        });
        if (rng.ok || rng.status === 206) return true;
      } catch {}
      await new Promise(r => setTimeout(r, delayMs));
    }
    return false;
  }

  const retryVideoSrc = (el: HTMLVideoElement, maxRetries = 4) => {
  // Trzymamy licznik w data-atrybucie, żeby nie robić stanu per kafelek
    const count = Number(el.dataset.retry || '0');
    if (count >= maxRetries) return;

    setTimeout(() => {
      const base = el.src.split('?')[0];     // usuń stare query
      el.dataset.retry = String(count + 1);
      el.src = `${base}?t=${Date.now()}`;    // cache-buster
      el.load();                             // zainicjuj ponowne pobranie
    }, 700 + count * 500); // rosnący odstęp
  };

  const openFullscreen = (index: number) => {
    if (selectMode) return;
    setFullscreenIndex(index);
  };

  const closeFullscreen = () => {
    setFullscreenIndex(null);
  };

  const showPrev = (e?: React.MouseEvent | TouchEvent) => {
    e?.stopPropagation?.();
    setFullscreenIndex((prev) => (prev !== null ? Math.max(prev - 1, 0) : null));
  };

  const showNext = (e?: React.MouseEvent | TouchEvent) => {
    e?.stopPropagation?.();
    setFullscreenIndex((prev) => (prev !== null ? Math.min(prev + 1, media.length - 1) : null));
  };

  const toggleSelectMode = () => {
    setSelectMode((prev) => !prev);
    setSelectedItems(new Set());
  };

  const toggleSelection = (index: number) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedItems((prev) => {
      if (prev.size === media.length) return new Set();
      return new Set(media.map((_, index) => index));
    });
  };

  const downloadSelected = async () => {
    const zip = new JSZip();
    selectedItems.forEach((index) => {
      const item = media[index];
      const ext = item.type === 'video' ? 'mp4' : 'jpg';
      zip.file(`media-${index}.${ext}`, fetch(item.url).then(res => res.blob()));
    });
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'media.zip');
    setSelectMode(false);
  };

  const fetchMedia = async () => {
    try {
      const response = await fetch('https://nataliadominik.pl/backend/list_files.php');
      const data = await response.json();
      setMedia(data);
    } catch (error) {
      console.error('Błąd pobierania plików:', error);
    }
  };

  useEffect(() => {
    if (!setFullscreenGallery) return;
    setFullscreenGallery(fullscreenIndex !== null);
  }, [fullscreenIndex, setFullscreenGallery]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (fullscreenIndex === null) return;
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
      if (e.key === 'Escape') closeFullscreen();
    };

    const SWIPE_THRESHOLD = 30;
    const handleTouchStart = (e: TouchEvent) => {
      // Ignoruj zdarzenia na karuzeli miniatur
      if (thumbnailsBarRef.current && thumbnailsBarRef.current.contains(e.target as Node)) {
        return;
      }
      if (e.target === overlayRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // Ignoruj zdarzenia na karuzeli miniatur
      if (thumbnailsBarRef.current && thumbnailsBarRef.current.contains(e.target as Node)) {
        return;
      }
      if (e.target === overlayRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (touchStartX.current === null) return;
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX.current - touchEndX;
      if (Math.abs(diff) > SWIPE_THRESHOLD) {
        diff > 0 ? showNext() : showPrev();
      }
      touchStartX.current = null;
    };

    // Zablokuj przewijanie strony
    if (fullscreenIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    window.addEventListener('keydown', handleKeyDown);
    const overlay = overlayRef.current;
    if (fullscreenIndex !== null && overlay) {
      overlay.addEventListener('touchstart', handleTouchStart, { passive: false });
      overlay.addEventListener('touchend', handleTouchEnd, { passive: false });
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      const overlay = overlayRef.current;
      if (overlay) {
        overlay.removeEventListener('touchstart', handleTouchStart);
        overlay.removeEventListener('touchend', handleTouchEnd);
      }
      document.body.style.overflow = '';
    };
  }, [fullscreenIndex]);


  // --- ThumbnailsBar swipe/scroll logic ---
  useEffect(() => {
    const bar = thumbnailsBarRef.current;
    if (!bar) return;
    const updateScroll = () => {
      setCanScrollLeft(bar.scrollLeft > 0);
      setCanScrollRight(bar.scrollLeft + bar.offsetWidth < bar.scrollWidth - 1);
    };
    updateScroll();
    bar.addEventListener('scroll', updateScroll);
    window.addEventListener('resize', updateScroll);
    return () => {
      bar.removeEventListener('scroll', updateScroll);
      window.removeEventListener('resize', updateScroll);
    };
  }, [fullscreenIndex, media.length]);

  const scrollThumbs = (dir: 'left' | 'right') => {
    const bar = thumbnailsBarRef.current;
    if (!bar) return;
    const scrollAmount = bar.offsetWidth * 0.7;
    bar.scrollBy({ left: dir === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const response = await fetch('https://nataliadominik.pl/backend/list_files.php');
        const data = await response.json();
        setMedia(data);
      } catch (error) {
        console.error('Błąd pobierania plików:', error);
      }
    };

    fetchMedia();
  }, []);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Nieznany';
    return date.toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Wrapper>
      <TopBar>
        <UploadLabel>
          <Icon src={uploadIcon} alt="upload" />
          Dodaj zdjęcie / film
          <UploadInput type="file" accept="image/*,video/*" multiple onChange={handleUpload} />
          {(isUploading || errorBarVisible) && (
            <motion.div
              style={{
                position: 'absolute',
                left: 10,
                right: 10,
                bottom: 6,
                height: 3,
                borderRadius: 999,
                background: errorBarVisible ? '#f9e0de' : '#e1f5e6', // pastel tor dla błędu vs zielony tor
                overflow: 'hidden'
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                key={errorBarVisible ? 'err' : 'ok'}
                style={{
                  height: '100%',
                  borderRadius: 999,
                  background: errorBarVisible
                    ? 'linear-gradient(90deg,#e59b94,#d77972)' // czerwony błąd
                    : 'linear-gradient(90deg,#6fcf97,#27ae60)' // zielony gradient
                }}
                initial={{ width: '0%' }}
                animate={
                  errorBarVisible
                    ? { width: '100%', x: [0, 2, -2, 1, 0] }
                    : { width: `${Math.min(100, Math.max(0, uploadProgress))}%` }
                }
                transition={
                  errorBarVisible
                    ? { duration: 0.55, ease: 'easeInOut' }
                    : { duration: 0.2, ease: 'easeOut' }
                }
              />
            </motion.div>
          )}
        </UploadLabel>
        <AnimatePresence>
          {uploadError && (
            <Toast
              key="err-toast"
              initial={{ y: -6, opacity: 0 }}
              animate={{ y: 0,  opacity: 1 }}
              exit={{    y: -6, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 420, damping: 30 }}
              role="alert"
              aria-live="assertive"
            >
              <ToastContent>
                {uploadError} — spróbuj ponownie
                <CloseX onClick={() => setUploadError(null)} aria-label="Zamknij">×</CloseX>
              </ToastContent>

              <ToastTimer
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0 }}
                transition={{ duration: TOAST_MS / 1000, ease: 'linear' }}
              />
            </Toast>
          )}
        </AnimatePresence>
        <AnimatePresence mode="wait">
          {selectMode ? (
            <motion.button
              key="exit"
              onClick={toggleSelectMode}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              style={{
                backgroundColor: '#fee2e2',
                color: '#b91c1c',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                height: '40px',
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'center',
              }}
            >
              ✕ Wyjdź z trybu pobierania
            </motion.button>
          ) : (
            <motion.button
              key="download"
              onClick={toggleSelectMode}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.25 }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
              }}
            >
              <Icon src={downloadIcon} alt="Pobierz" />
            </motion.button>
          )}
        </AnimatePresence>

      </TopBar>
      <GalleryGrid single={media.length === 1}>
        {Array.isArray(media) && media.map((item, index) => (
          <MediaCard
            key={index}
            onClick={() => selectMode ? toggleSelection(index) : openFullscreen(index)}
          >
            {item.type === 'image' ? (
              <img src={item.url} alt={`media-${index}`} />
            ) : (
              <video
              src={item.url}
              controls
              muted
              preload="metadata"
              onError={(e) => retryVideoSrc(e.currentTarget)}
              poster={posterUrlFromVideo(item.url)}
            />
            )}
            <Overlay>{formatTimestamp(item.timestamp)}</Overlay>
            {selectMode && (
              <Checkbox>
                <StyledCheckbox
                  type="checkbox"
                  checked={selectedItems.has(index)}
                  onChange={() => toggleSelection(index)}
                  onClick={(e) => e.stopPropagation()}
                />
              </Checkbox>
            )}
          </MediaCard>
        ))}
      </GalleryGrid>
      {selectMode && (
        <FloatingDownloadBar>
          <DownloadButton onClick={downloadSelected}>Pobierz zaznaczone ({selectedItems.size})</DownloadButton>
          <SelectAllButton onClick={selectAll}>
            {selectedItems.size === media.length ? 'Odznacz wszystko' : 'Zaznacz wszystko'}
          </SelectAllButton>
        </FloatingDownloadBar>
      )}
      {fullscreenIndex !== null && (
        <FullscreenOverlay ref={overlayRef} onClick={() => {
          if (window.innerWidth >= 769) closeFullscreen();
        }}>
          <CloseButton onClick={closeFullscreen}>×</CloseButton>
          <FullscreenContent onClick={e => e.stopPropagation()}>
            {media[fullscreenIndex].type === 'image' ? (
              <FullscreenMedia as="img" src={media[fullscreenIndex].url} alt="fullscreen" />
            ) : (
              <FullscreenMedia as="video" src={media[fullscreenIndex].url} controls autoPlay />
            )}
            <ArrowLeft className="desktop-arrow" onClick={showPrev}>❮</ArrowLeft>
            <ArrowRight className="desktop-arrow" onClick={showNext}>❯</ArrowRight>
          </FullscreenContent>
          <ThumbnailsWrapper>
            {window.innerWidth >= 769 && canScrollLeft && (
              <ThumbsArrowLeft onClick={e => { e.stopPropagation(); scrollThumbs('left'); }}>❮</ThumbsArrowLeft>
            )}
            <ThumbnailsBar ref={thumbnailsBarRef}>
              {media.map((item, idx) => (
                <Thumb
                  key={idx}
                  $active={idx === fullscreenIndex}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setFullscreenIndex(idx);
                  }}
                  as={item.type === 'image' ? 'img' : 'video'}
                  src={item.url}
                  {...(item.type === 'video' ? { controls: false } : {})}
                  alt={item.type + '-' + idx}
                />
              ))}
            </ThumbnailsBar>
            {window.innerWidth >= 769 && canScrollRight && (
              <ThumbsArrowRight onClick={e => { e.stopPropagation(); scrollThumbs('right'); }}>❯</ThumbsArrowRight>
            )}
          </ThumbnailsWrapper>

        </FullscreenOverlay>
      )}
    </Wrapper>
  );
};

const Toast = styled(motion.div)`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 9999;

  background: #fff;
  border: 1px solid #ead1cc;      /* spójne z Twoją paletą */
  border-radius: 12px;
  color: #5b4f4d;
  box-shadow: 0 6px 18px rgba(0,0,0,.08);
  overflow: hidden;               /* żeby timer był równo z krawędzią */
`;

const ToastContent = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  font-size: .85rem;
  line-height: 1.15;
`;

const CloseX = styled.button`
  all: unset; cursor: pointer;
  padding: 2px 4px; border-radius: 8px;
  opacity: .6;
  &:hover { background: #f6e6e3; opacity: .9; }
`;

const ToastTimer = styled(motion.div)`
  position: absolute; left: 0; right: 0; bottom: 0;
  height: 2px;                       /* smukły */
  background: linear-gradient(90deg, #ef8f84, #e66e61);
  transform-origin: left center;     /* idzie od lewej do prawej */
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
`;

const TopBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ProgressArea = styled.div`
  flex: 0 0 clamp(0px, 33vw, 240px); /* dopasowuje się na mobile, max ~240px */
  display: flex;
  justify-content: flex-start;
`;

const UploadLabel = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f4e4e1;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  color: #444;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
`;

const Icon = styled.img`
  width: 20px;
  height: 20px;
`;

const UploadInput = styled.input`
  display: none;
`;

const GalleryGrid = styled.div<{ single: boolean }>`
  display: grid;
  grid-template-columns: ${({ single }) => single ? '1fr' : 'repeat(auto-fit, minmax(140px, 1fr))'};
  gap: 12px;
  width: 100%;
  max-width: ${({ single }) => single ? '400px' : '800px'};
`;

const MediaCard = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid #ddd;
  cursor: pointer;
  transition: transform .2s ease-in-out;
  aspect-ratio: 4 / 3;      /* ← wszystkie kafelki mają ten sam kształt */
  background: #0b0b0b;      /* tło pod video zamiast białych pasów */

  &:hover { transform: scale(1.01); }

  /* media wypełniają kartę */
  img, video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;      /* ← przytnij zamiast pasków */
    display: block;
  }
`;

const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(to top, rgba(0,0,0,0.6), transparent);
  color: #fff;
  padding: 4px 8px;
  font-size: 0.75rem;
`;

const Checkbox = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
`;

const StyledCheckbox = styled.input`
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid #fff;
  border-radius: 4px;
  background-color: rgba(0,0,0,0.6);
  cursor: pointer;
  display: grid;
  place-content: center;
  transition: all 0.2s ease;

  &:checked {
    background-color: #2563eb;
    border-color: #2563eb;
  }

  &:checked::after {
    content: '✔';
    color: white;
    font-size: 12px;
  }
`;

const FloatingDownloadBar = styled.div`
  position: fixed;
  bottom: 16px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 12px;
  z-index: 1000;
  pointer-events: none;
`;

const DownloadButton = styled.button`
  background-color: #2563eb;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: bold;
  border: none;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  cursor: pointer;
  pointer-events: all;

  &:hover {
    background-color: #1e40af;
  }
`;

const SelectAllButton = styled(DownloadButton)`
  background-color: #4b5563;

  &:hover {
    background-color: #374151;
  }
`;

const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

const FullscreenContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FullscreenMedia = styled.img`
  width: auto;
  max-width: 90vw;
  max-height: 90dvh;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0,0,0,0.5);
`;

const ArrowBase = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  padding: 0 12px;
  cursor: pointer;
  user-select: none;
  z-index: 10;
  @media (min-width: 769px) {
    min-width: 3rem;
    min-height: 3rem;
    padding: 1rem;
    margin: 0 1rem;
  }
  &:hover {
    color: #ffd6d6;
  }
`;

const ArrowLeft = styled(ArrowBase)`
  left: -80px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const ArrowRight = styled(ArrowBase)`
  right: -80px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const ThumbnailsBar = styled.div`
  display: flex;
  gap: 8px;
  background: rgba(0,0,0,0.5);
  padding: 8px 16px;
  border-radius: 12px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
  flex: 1;
  max-width: 80vw;
  width: auto;
  min-width: 0;
  justify-content: center;
`;

const ThumbnailsWrapper = styled.div`
  position: absolute;
  bottom: 45px;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  max-width: 80vw;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const ThumbsArrowLeft = styled.button`
  display: none;
  @media (min-width: 769px) {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: #fff;
    font-size: 1.7rem;
    cursor: pointer;
    padding: 0 8px;
    margin-right: 4px;
    opacity: 0.7;
    transition: opacity 0.2s;
    &:hover { opacity: 1; }
  }
`;
const ThumbsArrowRight = styled(ThumbsArrowLeft)`
  margin-left: 4px;
  margin-right: 0;
`;

const Thumb = styled.img<{ $active: boolean }>`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  border: 2px solid ${({ $active }) => ($active ? '#fff' : 'transparent')};
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  opacity: ${({ $active }) => ($active ? 1 : 0.7)};
  cursor: pointer;
  transition: border 0.2s, opacity 0.2s;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0,0,0,0.6);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
  cursor: pointer;

  &:hover {
    background: rgba(255,255,255,0.2);
  }
`;

export default Gallery;